import { keepPreviousData } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { atom, useAtomValue } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { getCurrentKYCStatusApi } from '@flipster/apis/v1/kyc';
import {
  KycClientStatusEnum,
  KycLevelEnum,
  KycLevelStatusEnum,
  KycLevelType,
  UserKycStatus,
} from '@flipster/apis/v1/kyc/types';
import { User } from '@flipster/apis/v1/user/types';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

import { authBunja } from '../auth/state';

export type KycWarningStatus = 'RequireNextLevel' | 'InProgress' | 'Resubmit' | 'Rejected' | 'TncAgreementRequired';

const levelMap: Record<KycLevelType, number> = {
  LEVEL_0: 0,
  LEVEL_1: 1,
  LEVEL_2_SIMPLE: 2,
  LEVEL_2: 2,
  LEVEL_3: 3,
  LEVEL_4: 4,
};

export const getNextLevel = (currentLevel: KycLevelType, showLevel2Simple?: boolean) => {
  if (currentLevel === KycLevelEnum.Level1) {
    if (showLevel2Simple) return KycLevelEnum.Level2Simple;
    return KycLevelEnum.Level2;
  }
  if (currentLevel === KycLevelEnum.Level0) return KycLevelEnum.Level1;
  if (currentLevel === KycLevelEnum.Level2) return KycLevelEnum.Level3;
  if (currentLevel === KycLevelEnum.Level3) return KycLevelEnum.Level4;
  if (currentLevel === KycLevelEnum.Level4) return null;
  return null;
};

export const getLevelByNumber = (level: KycLevelType) => {
  return levelMap[level];
};

export const getWarningStatus = (
  user: User | undefined,
  kycStatus: UserKycStatus | undefined,
  isAchievedLevel: boolean,
): KycWarningStatus | undefined => {
  // ref: https://www.notion.so/prextech/Web-onboarding-process-improvement-120eb32ad227804c89d4c9544bcd8c7b?pvs=4#120eb32ad22780b8afd6fa915cd8ee13
  if (!user || !kycStatus) return undefined;

  if (!isAchievedLevel) {
    const lowestNotCompletedLevelStatus = [kycStatus.level2Status, kycStatus.level3Status, kycStatus.level4Status].find(
      (status) => status !== 'COMPLETED',
    );
    // 우선순위대로 정렬되어 있음
    if (lowestNotCompletedLevelStatus === 'REJECTED') {
      return 'Rejected';
    }
    if (lowestNotCompletedLevelStatus === 'RESUBMIT') {
      return 'Resubmit';
    }
    if (kycStatus.tncAgreementRequired) {
      return 'TncAgreementRequired';
    }
    if (lowestNotCompletedLevelStatus === 'IN_REVIEW') {
      return 'InProgress';
    }
    if (lowestNotCompletedLevelStatus === 'NOT_STARTED' || lowestNotCompletedLevelStatus === 'IN_PROGRESS') {
      return 'RequireNextLevel';
    }
  }
};

const kycPlaceholderData: UserKycStatus = {
  currentKYCLevel: KycLevelEnum.Level0,
  desirableKYCLevel: KycLevelEnum.Level4,
  level2SimpleStatus: KycLevelStatusEnum.NotStarted,
  level2Status: KycLevelStatusEnum.NotStarted,
  level3Status: KycLevelStatusEnum.NotStarted,
  level4Status: KycLevelStatusEnum.NotStarted,
  showLevel2Simple: false,
  enableLevel2Simple: false,
  enableLevel2: false,
  enableLevel3: false,
  enableLevel4: false,
  clientStatus: KycClientStatusEnum.None,
  legacyKYCLevel: KycLevelEnum.Level0,
  tncAgreementRequired: false,
};

export const kycBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { userAtom, isLoggedInAtom } = bunja.use(authBunja);

  const getCurrentKycStatusApiAtom = atomWithQuery((get) => {
    return {
      queryKey: ['getCurrentKYCStatusApi'],
      queryFn: getCurrentKYCStatusApi,
      select: (res: AxiosResponse<UserKycStatus, unknown>) => res.data,
      enabled: get(isLoggedInAtom),
      refetchOnMount: true,
      placeholderData: keepPreviousData,
      retry: 1,
      retryDelay: 1000 * 3,
      experimental_prefetchInRender: true,
    };
  });

  const isLoadingAtom = atom((get) => get(getCurrentKycStatusApiAtom).isLoading);
  const isFetchedAtom = atom((get) => get(getCurrentKycStatusApiAtom).isFetched);
  const kycStatusAtom = atom((get) => get(getCurrentKycStatusApiAtom).data);
  const kycStatusWithDefaultAtom = atom((get) => get(kycStatusAtom) ?? kycPlaceholderData);
  const isAchievedLevelAtom = atom((get) => {
    const kycStatus = get(kycStatusWithDefaultAtom);
    return (
      kycStatus!.currentKYCLevel >= kycStatus!.desirableKYCLevel &&
      kycStatus?.clientStatus === KycClientStatusEnum.Active
    );
  });
  const isRequireKycAtom = atom((get) => {
    const isFetched = get(getCurrentKycStatusApiAtom).isFetched;
    const isAchievedLevel = get(isAchievedLevelAtom);
    return isFetched && !isAchievedLevel;
  });
  const isInGracePeriodAtom = atom((get) => {
    const kycStatus = get(kycStatusWithDefaultAtom);
    return Boolean(kycStatus?.gracePeriod);
  });
  const nextLevelAtom = atom((get) => {
    const kycStatus = get(kycStatusWithDefaultAtom);
    return kycStatus ? getNextLevel(kycStatus.currentKYCLevel, kycStatus.showLevel2Simple) : null;
  });
  const warningStatusAtom = atom((get) => {
    const kycStatus = get(kycStatusWithDefaultAtom);
    const user = get(userAtom);
    const isAchievedLevel = get(isAchievedLevelAtom);
    const isFetched = get(getCurrentKycStatusApiAtom).isFetched;
    return isFetched ? getWarningStatus(user, kycStatus, isAchievedLevel) : undefined;
  });
  const promiseAtom = atom((get) => ({ promise: get(getCurrentKycStatusApiAtom).promise }));
  function refetch() {
    const { refetch } = store.get(getCurrentKycStatusApiAtom);
    return refetch();
  }
  bunja.effect(() => store.sub(getCurrentKycStatusApiAtom, () => {}));
  return {
    isLoadingAtom,
    isFetchedAtom,
    kycStatusAtom,
    kycStatusWithDefaultAtom,
    isAchievedLevelAtom,
    isRequireKycAtom,
    isInGracePeriodAtom,
    nextLevelAtom,
    warningStatusAtom,
    refetch,
    promiseAtom,
  };
});
kycBunja.debugLabel = 'kycBunja';

export function useKYC() {
  const {
    isLoadingAtom,
    isFetchedAtom,
    kycStatusWithDefaultAtom,
    isAchievedLevelAtom,
    isRequireKycAtom,
    isInGracePeriodAtom,
    nextLevelAtom,
    warningStatusAtom,
    refetch,
  } = useBunja(kycBunja);
  const isLoading = useAtomValue(isLoadingAtom);
  const kycInfo = useAtomValue(kycStatusWithDefaultAtom);
  const isAchievedLevel = useAtomValue(isAchievedLevelAtom);
  const isInGracePeriod = useAtomValue(isInGracePeriodAtom);
  const isRequireKyc = useAtomValue(isRequireKycAtom);
  const isFetched = useAtomValue(isFetchedAtom);
  const nextLevel = useAtomValue(nextLevelAtom);
  const warningStatus = useAtomValue(warningStatusAtom);
  return {
    isLoading,
    kycInfo,
    refetch,
    isAchievedLevel,
    isInGracePeriod,
    isRequireKyc,
    isFetched,
    nextLevel,
    warningStatus,
  };
}
